import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Client } from 'boardgame.io/react';
import { SocketIO } from 'boardgame.io/multiplayer';
import { GuesshueGame } from '../shared/game';
import { GuesshueBoard } from './Board';
import { leaveRoom } from '../services/lobby';

export function GameRoom() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [playerID, setPlayerID] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<string | null>(null);

  useEffect(() => {
    // Check if user has a username
    const storedUsername = sessionStorage.getItem('username');
    if (!storedUsername) {
      navigate('/');
      return;
    }
    setUsername(storedUsername);
    
    // Get stored credentials for this room
    const storedPlayerID = sessionStorage.getItem(`guesshue-${roomId}-playerID`);
    const storedCredentials = sessionStorage.getItem(`guesshue-${roomId}-creds`);
    
    if (storedPlayerID && storedCredentials) {
      setPlayerID(storedPlayerID);
      setCredentials(storedCredentials);
    } else {
      // No credentials, redirect to lobby
      navigate('/lobby');
    }
  }, [navigate, roomId]);

  // Create multiplayer client component
  const GuesshueClient = useMemo(() => {
    if (!playerID || !credentials || !roomId) return null;
    
    console.log('Creating boardgame.io client with:', { roomId, playerID, hasCredentials: !!credentials });
    
    const serverUrl = import.meta.env.VITE_SERVER_URL || 
      (import.meta.env.DEV ? 'http://localhost:8185' : window.location.origin);
    console.log('Connecting to server:', serverUrl);
    
    // Create SocketIO instance with debugging
    const socketIO = SocketIO({ 
      server: serverUrl
    });
    
    // Log connection status
    console.log('SocketIO instance created:', socketIO);
    
    const client = Client({
      game: GuesshueGame,
      board: GuesshueBoard,
      multiplayer: socketIO,
      debug: import.meta.env.DEV, // Only show debug sidebar in development
    });
    
    return client;
  }, [playerID, credentials, roomId]);
  
  const handleLeaveGame = async () => {
    if (roomId) {
      await leaveRoom(roomId);
    }
    navigate('/lobby');
  };

  if (!GuesshueClient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Loading game...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="absolute top-4 left-4 bg-white rounded-lg shadow px-4 py-2">
        <p className="text-sm text-gray-600">Room: <span className="font-mono font-bold">{roomId}</span></p>
        <p className="text-sm text-gray-600">Player: <span className="font-medium">{username}</span></p>
      </div>
      
      {GuesshueClient && (
        <GuesshueClient 
          matchID={roomId}
          playerID={playerID || undefined}
          credentials={credentials || undefined}
        />
      )}
      
      <div className="absolute top-4 right-4">
        <button
          onClick={handleLeaveGame}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
        >
          Leave Game
        </button>
      </div>
    </div>
  );
}